import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { StatusChip } from "@/components/common/StatusChip";
import { EmptyState } from "@/components/common/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ShieldCheck, CheckCircle2, XCircle, History } from "lucide-react";
import { toast } from "sonner";

/** Compact decision badge (pass/approved / pending/review / rejected). */
function DecisionBadge({ decision }) {
  const d = String(decision || "").toLowerCase();
  const map = {
    approved: { label: "Pass", className: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.35)]" },
    pass: { label: "Pass", className: "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))] border-[hsl(var(--success)/0.35)]" },
    pending: { label: "Review", className: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.35)]" },
    review: { label: "Review", className: "bg-[hsl(var(--warning)/0.15)] text-[hsl(var(--warning))] border-[hsl(var(--warning)/0.35)]" },
    rejected: { label: "Rejected", className: "bg-[hsl(var(--destructive)/0.15)] text-[hsl(var(--destructive))] border-[hsl(var(--destructive)/0.35)]" },
  };
  const cfg = map[d] || { label: decision || "—", className: "bg-muted text-muted-foreground border-border" };
  return (
    <Badge variant="outline" className={`uppercase tracking-wide text-[10px] ${cfg.className}`} data-testid={`audit-decision-${d}`}>
      {cfg.label}
    </Badge>
  );
}

export default function AdminQueue() {
  const [tab, setTab] = useState("ballboys");
  const [bbItems, setBbItems] = useState(null);
  const [atItems, setAtItems] = useState(null);
  const [audits, setAudits] = useState(null);
  const [viewing, setViewing] = useState(null);

  async function loadBB() {
    const { data } = await api.get("/admin/verifications?status=pending");
    setBbItems(data.items);
  }
  async function loadAthletes() {
    const { data } = await api.get("/admin/athlete-verifications?status=pending");
    setAtItems(data.items);
  }
  async function loadAudits() {
    try {
      const { data } = await api.get("/admin/verification-audits?limit=100");
      setAudits(data.items || []);
    } catch (_) {
      setAudits([]);
    }
  }
  useEffect(() => { loadBB(); loadAthletes(); loadAudits(); }, []);

  // Quick lookup: latest audit per user (audits are sorted desc by created_at already).
  const latestAuditByUser = (audits || []).reduce((acc, a) => {
    if (!acc[a.user_id]) acc[a.user_id] = a;
    return acc;
  }, {});

  async function decideBB(id, approve) {
    const reason = approve ? null : prompt("Reason (optional)") || null;
    try {
      await api.post(`/admin/verifications/${id}`, { approve, reason });
      toast.success(approve ? "Approved" : "Rejected");
      setViewing(null);
      loadBB();
      loadAudits();
    } catch (e) { toast.error("Failed"); }
  }
  async function decideAthlete(id, approve) {
    const reason = approve ? null : prompt("Reason (optional)") || null;
    try {
      await api.post(`/admin/athlete-verifications/${id}`, { approve, reason });
      toast.success(approve ? "Approved" : "Rejected");
      setViewing(null);
      loadAthletes();
      loadAudits();
    } catch (e) { toast.error("Failed"); }
  }

  return (
    <div className="space-y-3" data-testid="admin-verification-queue">
      <h2 className="text-xl font-semibold bb-display flex items-center gap-2">
        <ShieldCheck className="h-5 w-5" /> Verification queue
      </h2>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="ballboys" data-testid="admin-tab-ballboys">Ball Boys {bbItems?.length ? `(${bbItems.length})` : ""}</TabsTrigger>
          <TabsTrigger value="athletes" data-testid="admin-tab-athletes">Athletes {atItems?.length ? `(${atItems.length})` : ""}</TabsTrigger>
          <TabsTrigger value="audits" data-testid="admin-tab-audits">Audits {audits?.length ? `(${audits.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="ballboys" className="mt-3 space-y-2">
          {bbItems === null ? <Skeleton className="h-24" />
          : bbItems.length === 0 ? <EmptyState icon={ShieldCheck} title="No pending ball boy verifications" />
          : bbItems.map((bb) => {
              const audit = latestAuditByUser[bb.id];
              return (
                <Card key={bb.id}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        {bb.photo && <img src={bb.photo} alt="" className="h-10 w-10 rounded-full object-cover border border-border" />}
                        <div>
                          <div className="font-medium">{bb.name}</div>
                          <div className="text-xs text-muted-foreground">{bb.email}</div>
                          <div className="text-xs text-muted-foreground">Submitted: {bb.verification_submitted_at ? new Date(bb.verification_submitted_at).toLocaleString() : "—"}</div>
                          {bb.verification_match?.similarity != null && (
                            <div className="text-xs mt-1" data-testid="ballboy-face-match-line">
                              Face match: <span className="font-semibold tabular-nums">{bb.verification_match.similarity}%</span>
                              <span className="text-muted-foreground ml-1">· {bb.verification_match.reason}</span>
                            </div>
                          )}
                          {audit && (
                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="ballboy-audit-chips">
                              <span className="text-[10px] uppercase text-muted-foreground">Audit:</span>
                              <DecisionBadge decision={audit.decision || audit?.face_match?.auto_decision} />
                              {audit?.face_match?.similarity != null && (
                                <span className="text-[11px] tabular-nums text-muted-foreground" data-testid="ballboy-audit-similarity">
                                  {audit.face_match.similarity}% match
                                </span>
                              )}
                              {audit?.id_check?.auto_decision && (
                                <span className="text-[11px] text-muted-foreground">
                                  · ID <DecisionBadge decision={audit.id_check.auto_decision} />
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <StatusChip status="pending" />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button size="sm" variant="secondary" onClick={() => setViewing({ type: "bb", data: bb, audit })} data-testid="admin-view-id-button">View ID</Button>
                      <Button size="sm" onClick={() => decideBB(bb.id, true)} data-testid="admin-approve-button"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>
                      <Button size="sm" variant="destructive" onClick={() => decideBB(bb.id, false)} data-testid="admin-reject-button"><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </TabsContent>

        <TabsContent value="athletes" className="mt-3 space-y-2">
          {atItems === null ? <Skeleton className="h-24" />
          : atItems.length === 0 ? <EmptyState icon={ShieldCheck} title="No pending athlete verifications" />
          : atItems.map((a) => {
              const audit = latestAuditByUser[a.id];
              return (
                <Card key={a.id}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-3">
                        {a.photo && <img src={a.photo} alt="" className="h-10 w-10 rounded-full object-cover border border-border" />}
                        <div>
                          <div className="font-medium">{a.name}</div>
                          <div className="text-xs text-muted-foreground">{a.email}</div>
                          <div className="text-xs text-muted-foreground">Submitted: {a.athlete_verification_submitted_at ? new Date(a.athlete_verification_submitted_at).toLocaleString() : "—"}</div>
                          {a.athlete_verification_match?.similarity != null && (
                            <div className="text-xs mt-1" data-testid="athlete-face-match-line">
                              Face match: <span className="font-semibold tabular-nums">{a.athlete_verification_match.similarity}%</span>
                              <span className="text-muted-foreground ml-1">· {a.athlete_verification_match.reason}</span>
                            </div>
                          )}
                          {audit && (
                            <div className="flex flex-wrap items-center gap-1.5 mt-1.5" data-testid="athlete-audit-chips">
                              <span className="text-[10px] uppercase text-muted-foreground">Audit:</span>
                              <DecisionBadge decision={audit.decision || audit?.face_match?.auto_decision} />
                              {audit?.face_match?.similarity != null && (
                                <span className="text-[11px] tabular-nums text-muted-foreground" data-testid="athlete-audit-similarity">
                                  {audit.face_match.similarity}% match
                                </span>
                              )}
                              {audit?.id_check?.auto_decision && (
                                <span className="text-[11px] text-muted-foreground">
                                  · ID <DecisionBadge decision={audit.id_check.auto_decision} />
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <StatusChip status="pending" />
                    </div>
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <Button size="sm" variant="secondary" onClick={() => setViewing({ type: "athlete", data: a, audit })} data-testid="admin-view-athlete-button">View ID + selfie</Button>
                      <Button size="sm" onClick={() => decideAthlete(a.id, true)} data-testid="admin-approve-athlete-button"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>
                      <Button size="sm" variant="destructive" onClick={() => decideAthlete(a.id, false)} data-testid="admin-reject-athlete-button"><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
        </TabsContent>

        <TabsContent value="audits" className="mt-3 space-y-2" data-testid="admin-audits-tab-content">
          {audits === null ? <Skeleton className="h-24" />
          : audits.length === 0 ? <EmptyState icon={History} title="No verification audits yet" />
          : (
            <div className="rounded-lg border border-border divide-y divide-border bg-card">
              {audits.map((a) => (
                <div key={a.id} className="p-3 flex items-center justify-between gap-3" data-testid={`audit-row-${a.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{a.user_name || a.user_id}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {new Date(a.created_at).toLocaleString()}
                      {a.kind ? <> · {a.kind.replace(/_/g, " ")}</> : null}
                      {a.decision_reason ? <> · {a.decision_reason}</> : null}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {a?.face_match?.similarity != null && (
                      <span className="text-[11px] tabular-nums text-muted-foreground" data-testid={`audit-similarity-${a.id}`}>
                        {a.face_match.similarity}%
                      </span>
                    )}
                    <DecisionBadge decision={a.decision} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Verification for {viewing?.data?.name}</DialogTitle></DialogHeader>
          {viewing?.type === "bb" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Government ID</div>
                  {viewing.data.id_photo ? <img src={viewing.data.id_photo} alt="ID" className="w-full rounded-lg border border-border" /> : <div className="text-xs">No ID</div>}
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Selfie</div>
                  {viewing.data.selfie ? <img src={viewing.data.selfie} alt="Selfie" className="w-full rounded-lg border border-border" /> : <div className="text-xs">No selfie</div>}
                </div>
              </div>
              {viewing.audit && (
                <div className="text-xs text-muted-foreground space-y-1" data-testid="ballboy-audit-details">
                  <div className="flex items-center gap-2">
                    <span>Audit decision:</span>
                    <DecisionBadge decision={viewing.audit.decision} />
                    {viewing.audit?.face_match?.similarity != null && (
                      <span className="tabular-nums">· {viewing.audit.face_match.similarity}% face match</span>
                    )}
                  </div>
                  {viewing.audit?.id_check?.flags?.length > 0 && (
                    <div>ID flags: {viewing.audit.id_check.flags.join(", ")}</div>
                  )}
                  {viewing.audit?.id_check?.top_text?.length > 0 && (
                    <div className="line-clamp-2">ID text: {viewing.audit.id_check.top_text.slice(0, 6).join(" · ")}</div>
                  )}
                </div>
              )}
            </div>
          )}
          {viewing?.type === "athlete" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Government ID</div>
                  {viewing.data.athlete_id_photo ? <img src={viewing.data.athlete_id_photo} alt="ID" className="w-full rounded-lg border border-border" /> : <div className="text-xs">No ID</div>}
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Selfie</div>
                  {viewing.data.athlete_selfie ? <img src={viewing.data.athlete_selfie} alt="Selfie" className="w-full rounded-lg border border-border" /> : <div className="text-xs">No selfie</div>}
                </div>
                {viewing.data.address && <div className="col-span-2 text-xs text-muted-foreground">Address: {viewing.data.address}</div>}
              </div>
              {viewing.audit && (
                <div className="text-xs text-muted-foreground space-y-1" data-testid="athlete-audit-details">
                  <div className="flex items-center gap-2">
                    <span>Audit decision:</span>
                    <DecisionBadge decision={viewing.audit.decision} />
                    {viewing.audit?.face_match?.similarity != null && (
                      <span className="tabular-nums">· {viewing.audit.face_match.similarity}% face match</span>
                    )}
                  </div>
                  {viewing.audit?.id_check?.flags?.length > 0 && (
                    <div>ID flags: {viewing.audit.id_check.flags.join(", ")}</div>
                  )}
                  {viewing.audit?.id_check?.top_text?.length > 0 && (
                    <div className="line-clamp-2">ID text: {viewing.audit.id_check.top_text.slice(0, 6).join(" · ")}</div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="destructive" onClick={() => viewing?.type === "bb" ? decideBB(viewing.data.id, false) : decideAthlete(viewing.data.id, false)}>Reject</Button>
            <Button onClick={() => viewing?.type === "bb" ? decideBB(viewing.data.id, true) : decideAthlete(viewing.data.id, true)}>Approve</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
